import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LogOut, LayoutDashboard, Building2, Search, Briefcase } from "lucide-react";
import { RelevoLogo, RelevoMark } from "./RelevoLogo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { t } from "../lib/i18n";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSeller = user?.role === "seller";
  const isSearcher = user?.role === "searcher";
  const isInvestor = user?.role === "investor";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="hairline-b bg-bone sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-home-link">
            <RelevoLogo className="text-2xl" />
            <span className="font-mono-label text-muted-foreground hidden md:inline">/ {t("ES")}</span>
          </Link>

          <nav className="flex items-center gap-1">
            {user && isSeller && (
              <>
                <NavLink to="/seller/dashboard" active={location.pathname.startsWith("/seller/dashboard")} icon={<LayoutDashboard className="w-4 h-4" />} testid="nav-seller-dashboard">
                  {t("Panel")}
                </NavLink>
                <NavLink to="/seller/companies" active={location.pathname.startsWith("/seller/companies") || location.pathname.includes("/seller/company")} icon={<Building2 className="w-4 h-4" />} testid="nav-seller-companies">
                  {t("Mis Empresas")}
                </NavLink>
              </>
            )}
            {user && isSearcher && (
              <>
                <NavLink to="/searcher/marketplace" active={location.pathname.startsWith("/searcher/marketplace") || location.pathname.includes("/searcher/company")} icon={<Search className="w-4 h-4" />} testid="nav-searcher-market">
                  {t("Marketplace")}
                </NavLink>
                <NavLink to="/searcher/pipeline" active={location.pathname.startsWith("/searcher/pipeline")} icon={<Briefcase className="w-4 h-4" />} testid="nav-searcher-pipeline">
                  {t("Pipeline")}
                </NavLink>
              </>
            )}
            {user && isInvestor && (
              <>
                <NavLink to="/investor/marketplace" active={location.pathname.startsWith("/investor/marketplace") || location.pathname.includes("/investor/opportunity")} icon={<Search className="w-4 h-4" />} testid="nav-investor-market">
                  {t("Oportunidades")}
                </NavLink>
                <NavLink to="/investor/portfolio" active={location.pathname.startsWith("/investor/portfolio")} icon={<Briefcase className="w-4 h-4" />} testid="nav-investor-portfolio">
                  {t("Cartera")}
                </NavLink>
              </>
            )}
            <LanguageSwitcher />
            {!user && (
              <Button
                onClick={() => navigate("/login")}
                className="rounded-none bg-[#C25F1E] hover:bg-[#A94F15] text-white font-semibold h-9 px-5"
                data-testid="header-login-btn"
              >
                {t("Iniciar sesión")}
              </Button>
            )}
            {user && (
              <div className="flex items-center gap-3 ml-3 pl-3 hairline border-r-0 border-t-0 border-b-0">
                <div className="hidden sm:flex flex-col items-end leading-snug py-1">
                  <span className="text-sm font-medium">{user.name}</span>
                  <span className="font-mono-label text-muted-foreground">{user.role || "sin rol"}</span>
                </div>
                {user.picture && (
                  <img src={user.picture} alt="" className="w-8 h-8 hairline" data-testid="user-avatar" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="rounded-none hover:bg-secondary"
                  data-testid="header-logout-btn"
                  title={t("Cerrar sesión")}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="hairline-t mt-16 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap justify-between gap-4 text-sm text-muted-foreground">
          <span className="font-mono-label flex items-center gap-2">
            <RelevoMark size="16px" />{" "}{t("© 2026 RELEVO · Plataforma de Search Funds")}
          </span>
          <span className="font-mono-label flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/aviso-legal" className="hover:text-foreground transition-colors" data-testid="footer-legal-link">
              {t("Aviso Legal")}
            </Link>
            <Link to="/privacidad" className="hover:text-foreground transition-colors" data-testid="footer-privacy-link">
              {t("Política de Privacidad")}
            </Link>
            <Link to="/cookies" className="hover:text-foreground transition-colors" data-testid="footer-cookies-link">
              {t("Política de Cookies")}
            </Link>
            <span>{t("Madrid · Barcelona")}</span>
          </span>
        </div>
      </footer>
    </div>
  );
}

const NavLink = ({ to, active, icon, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 h-9 text-sm font-medium hairline ${
      active
        ? "bg-[#1E3A5F] text-bone border-[#1E3A5F]"
        : "border-transparent hover:border-border hover:bg-secondary/50"
    }`}
  >
    {icon}
    <span className="hidden sm:inline">{children}</span>
  </Link>
);
